import React from 'react';
import { hysteriFetch } from 'hysterics';
import { useSelector } from 'react-redux';
import { useLabels, useRequestData } from 'wsm-common-data';
import { formatCurrency } from 'ddc-intl';
import { trackEvent } from '../utilities/tracking';
import { appleWalletComponentProps } from '../utilities/commonPropTypes';

const AppleWalletButton = ({
	title,
	description,
	disclaimer,
	discountValue,
	discountType,
	endDateDisplay,
	tabIndexBase,
	id,
	promoDetails,
	promoOemId,
	incentive = {},
	applicableVehicle = ''
}) => {
	const requestUrl = '/api/widget/ws-specials/pass';
	const fetchApplePass = hysteriFetch('ws-specials-apple-pass');
	const accountName = useSelector((state) => state.account.name);
	const labels = useLabels();
	const offLabel = labels.get('OFF');
	const { widgetName, windowId, locale } = useRequestData();
	let discountDisplay;
	let formattedDiscountValue;
	switch (discountType) {
		case 'PERCENT':
			discountDisplay = `${discountValue}% ${offLabel}`;
			break;
		case 'AMOUNT':
			formattedDiscountValue = formatCurrency(
				discountValue,
				locale,
				true
			);
			discountDisplay = `${formattedDiscountValue} ${offLabel}`;
			break;
		case 'FIXED':
			discountDisplay = formatCurrency(discountValue, locale, true);
			break;
		case 'STARTING_AT':
			formattedDiscountValue = formatCurrency(
				discountValue,
				locale,
				true
			);
			discountDisplay = `${labels.get('STARTING_AT_SCM_UI')} ${formattedDiscountValue} `;
			break;
		default:
			discountDisplay = null;
			break;
	}
	let discountLabel =
		discountDisplay && discountType === 'FIXED'
			? labels.get('PRICE')
			: labels.get('SPECIALS_DISCOUNT');

	// Re-mapping data when incentive is available
	if (incentive !== null && Object.keys(incentive).length) {
		/* eslint-disable no-param-reassign */
		const { shortTitle, offerDetails, expirationDate } = incentive;
		title = shortTitle;
		discountDisplay = applicableVehicle;
		description = offerDetails;
		endDateDisplay = expirationDate;
		disclaimer = incentive.disclaimer;
		discountLabel = '';
	}

	const handleClick = () => {
		trackEvent({
			widgetName,
			windowId,
			element: 'Add to Apple Wallet Button',
			result: 'Offer added to Apple Wallet',
			oemId: promoOemId
		});

		fetchApplePass(requestUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				parameters: {
					title,
					description,
					disclaimer,
					discountDisplay: discountDisplay || '',
					accountName,
					endDateDisplay,
					id,
					promoDetails,
					titleLabel: labels.get('TITLE'),
					offerDetails: labels.get('OFFER_DETAILS'),
					disclaimerLabel: labels.get('DISCLAIMERS'),
					descriptionLabel: labels.get('DESCRIPTION'),
					discountLabel: discountDisplay ? discountLabel : ''
				}
			})
		})
			.then((response) => {
				const data = Uint8Array.from(response.body.data);
				const content = new Blob([data.buffer], {
					type: 'application/vnd.apple.pkpass'
				});
				const encodedUri = window.URL.createObjectURL(content);
				const link = document.createElement('a');

				link.setAttribute('href', encodedUri);
				link.setAttribute('download', 'promo.pkpass');

				link.click();
			})
			.catch((e) => {
				throw new Error(`Apple Pass fetch error: ${e}`);
			});
	};

	const button = (
		<button
			type="button"
			tabIndex={tabIndexBase + 6}
			onClick={handleClick}
			className="btn btn-link d-block p-0 mb-4"
		>
			<img
				src={`https://images.dealer.com/apple/pass/templates/${locale}/apple-logo.svg`}
				alt="Apple Wallet Logo"
			/>
		</button>
	);

	return button;
};

AppleWalletButton.propTypes = appleWalletComponentProps;

export default AppleWalletButton;
